import { arrayAdd, arrayUpdate } from '@datorama/akita';
import type { AxiosInstance } from 'axios';
import { type Attachment } from 'types/models';

async function uploadFile(
   httpClient: AxiosInstance,
   version: string,
   entityName: string,
   entityId: string | number,
   file: File,
   organisationId: string | number,
   from: string,
   to: string,
   description?: string,
   supplierId?: number
): Promise<any> {
   const formData = new FormData();
   formData.append('attachment', file);
   formData.append('organisationId', String(organisationId));
   formData.append('from', from);
   formData.append('to', to);

   if (supplierId) {
      formData.append('supplierId', supplierId.toString());
   }

   if (description) {
      formData.append('description', description);
   }

   const response = await httpClient.post(`/${version}/${entityName}/${entityId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
   });
   // catch is handled in the caller function
   return response.data;
}

async function updateFile(
   httpClient: AxiosInstance,
   version: string,
   entityName: string,
   entityId: string | number,
   attachmentId: number,
   name: string,
   description?: string
): Promise<any> {
   const body: { description?: string; name: string } = { name };

   if (description !== undefined) {
      body.description = description;
   }

   const response = await httpClient.patch(`/${version}/${entityName}/${entityId}/attachments/${attachmentId}`, body);
   return response.data;
}

interface UploadFilesOptions {
   httpClient: AxiosInstance;
   version: string;
   entityName: string;
   entityId: string | number;
   organisationId: string | number;
   files: File[];
   from: string;
   to: string;
   description?: string;
   store: any;
   setError?: (_error: any) => void;
   storeUpdaterFn?: (_entity: any, _data: any) => void;
   supplierId?: number;
}

/**
 * Common utility function to upload multiple files to an entity.
 *
 * @param options An object containing all parameters needed for uploading files.
 */
export async function uploadFilesCommon(options: UploadFilesOptions): Promise<Attachment[]> {
   const { httpClient, version, entityName, entityId, files, organisationId, from, to, store, setError, description, storeUpdaterFn } = options;

   // Use applyTransaction to batch state updates

   return Promise.all(
      files.map((file) =>
         uploadFile(httpClient, version, entityName, entityId, file, organisationId, from, to, description)
            .then((data) => {
               // Update the store with the newly uploaded file data
               if (storeUpdaterFn) {
                  store.update(entityId, (entity: any) => storeUpdaterFn(entity, data));
               } else {
                  store.update(entityId, ({ attachments }: { attachments: any[] }) => ({
                     attachments: arrayAdd(attachments, data),
                  }));
               }

               return data;
            })
            .catch((error) => {
               if (setError) {
                  setError(error);
               }
            })
      )
   );
}

interface UpdateFileOptions {
   httpClient: AxiosInstance;
   version: string;
   entityName: string;
   entityId: number;
   attachmentId: number;
   name: string;
   description?: string;
   store: any;
   setError?: (error: any) => void;
}

export async function updateFileCommon(options: UpdateFileOptions): Promise<Attachment | undefined> {
   const { httpClient, version, entityName, entityId, attachmentId, description, store, setError, name } = options;

   try {
      const data = await updateFile(httpClient, version, entityName, entityId, attachmentId, name, description);
      // Update the store with the newly uploaded file data
      store.update(entityId, ({ attachments }: { attachments: any[] }) => ({
         attachments: arrayUpdate(attachments, attachmentId, data),
      }));
      return data;
   } catch (error) {
      if (setError) {
         setError(error);
      }
      return undefined;
   }
}
